import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { getUser } from '../api/leaguesApi';
import { getAuthUser } from '../redux/services/authServices';
import { isEmailVerificationRequired } from '../utils/emailVerification';
import {
    isEmailVerificationPromptExcludedPath,
    isEmailVerificationPromptFlagged,
    isImpersonatedSession,
} from '../utils/emailVerificationPrompt';
import {
    clearSignedIn,
    markDismissed,
    readPromptCookies,
    signInCookieMatchesMember,
} from '../utils/emailVerificationPromptCookies';

const NO_MEMBER = null;

// What the watcher remembers across renders within one page life. It lives in a ref so reading and
// updating it never re-renders the app root.
const createPromptTracker = () => ({
    openMemberId: NO_MEMBER,
    closedMemberIds: new Set(),
    shownWithoutCookies: false,
});

const setOpenMember = (tracker, setPromptMemberId, memberId) => {
    tracker.openMemberId = memberId;
    setPromptMemberId(memberId);
};

// Refreshes the member after a successful verify so emailVerificationPrompt comes back false. Deliberately
// not getAuthUser: its failure path nulls the member and sends them to login, which must not follow an
// "Email Verified" toast. On failure the current member stays in place; the next page load refreshes anyway.
const refreshMemberAfterVerify = async (dispatch) => {
    try {
        const response = await getUser();
        dispatch(getAuthUser.fulfilled(response.data, 'email-verification-refresh'));
    } catch (error) {
        console.error('[EmailVerificationPrompt] Member refresh after verify failed', { status: error?.response?.status ?? null });
    }
};

const shouldShowFromCookies = (tracker, memberId) => {
    const { readable, signIn, dismissed } = readPromptCookies(memberId);
    const signInCookieIsReason = readable && signInCookieMatchesMember(signIn, memberId);

    // Never re-open in the same page life after a dismiss or verify, unless a newer sign-in says so.
    if (tracker.closedMemberIds.has(memberId) && !signInCookieIsReason) {
        return false;
    }

    // Blocked cookies: no dismissal can be remembered, so show at most once per page load.
    if (!readable) {
        if (tracker.shownWithoutCookies) {
            return false;
        }

        tracker.shownWithoutCookies = true;
        return true;
    }

    if (!signInCookieIsReason && dismissed) {
        return false;
    }

    if (signInCookieIsReason) {
        clearSignedIn();
    }

    return true;
};

// One watcher per app (mounted in App.js). Re-evaluates on first load, member change and route change.
//
// Leagues has no in-page sign-in: signing in is a full-page trip through the legacy app, which sets the
// shared `utr_evp_signin` cookie. The none -> member change on every page load is just v1/auth/current
// resolving, so it is deliberately NOT treated as a sign-in here (unlike consumer web).
export const useEmailVerificationPrompt = () => {
    const user = useSelector((state) => state.auth?.user);
    const { pathname } = useLocation();
    const dispatch = useDispatch();

    const [promptMemberId, setPromptMemberId] = useState(NO_MEMBER);
    const [isToastOpen, setIsToastOpen] = useState(false);
    const trackerRef = useRef(null);

    if (trackerRef.current === null) {
        trackerRef.current = createPromptTracker();
    }

    const memberId = user?.id ?? NO_MEMBER;
    const impersonating = isImpersonatedSession(user);
    const verificationRequired = isEmailVerificationRequired(user);
    const promptFlagged = isEmailVerificationPromptFlagged(user);

    useEffect(() => {
        const tracker = trackerRef.current;

        if (tracker.openMemberId !== NO_MEMBER && tracker.openMemberId !== memberId) {
            setOpenMember(tracker, setPromptMemberId, NO_MEMBER);
        }

        // Not a dismissal: no cookie and no closed-member entry, so the prompt can come back once the
        // impersonation ends or the member leaves the excluded route.
        if (memberId === NO_MEMBER || impersonating || isEmailVerificationPromptExcludedPath(pathname)) {
            if (tracker.openMemberId !== NO_MEMBER) {
                setOpenMember(tracker, setPromptMemberId, NO_MEMBER);
            }

            return;
        }

        // Hard stop wins and ignores both cookies. OnboardingGuard already owns the redirect to the
        // verify screen (ENG-2801), so the prompt simply stays out of its way.
        if (verificationRequired || !promptFlagged) {
            if (tracker.openMemberId !== NO_MEMBER) {
                setOpenMember(tracker, setPromptMemberId, NO_MEMBER);
            }

            return;
        }

        if (tracker.openMemberId === memberId) {
            return;
        }

        if (shouldShowFromCookies(tracker, memberId)) {
            setOpenMember(tracker, setPromptMemberId, memberId);
        }
    }, [memberId, impersonating, verificationRequired, promptFlagged, pathname]);

    const dismiss = () => {
        const tracker = trackerRef.current;
        const id = tracker.openMemberId;

        if (id === NO_MEMBER) {
            return;
        }

        markDismissed(id);
        tracker.closedMemberIds.add(id);
        setOpenMember(tracker, setPromptMemberId, NO_MEMBER);
    };

    const completeVerification = async () => {
        const tracker = trackerRef.current;
        const id = tracker.openMemberId;

        if (id !== NO_MEMBER) {
            tracker.closedMemberIds.add(id);
        }

        setOpenMember(tracker, setPromptMemberId, NO_MEMBER);
        setIsToastOpen(true);
        await refreshMemberAfterVerify(dispatch);
    };

    const closeToast = () => {
        setIsToastOpen(false);
    };

    return {
        isOpen: promptMemberId !== NO_MEMBER && promptMemberId === memberId,
        isToastOpen,
        user,
        dismiss,
        completeVerification,
        closeToast,
    };
};
